/**
 * Where a file dialog is looking — pure helpers over a workspace's flat folder
 * list. No React.
 *
 * A location is a folder in one of the two browsable areas, or "Recent":
 *
 *   { kind: 'folder', area: 'documents',   folderId: null }  → Documents (the Holder root)
 *   { kind: 'folder', area: 'documents',   folderId: 'vdf…' } → a folder in Documents
 *   { kind: 'folder', area: 'arche-saves', folderId: null }  → Arche Saves (its root folder)
 *   { kind: 'folder', area: 'arche-saves', folderId: 'vdf…' } → e.g. Arche Saves / AI Images
 *   { kind: 'recent' }                                        → recently modified, across areas
 *
 * My-Fias is never a location: it holds arches' own internal files, which
 * neither dialog opens or saves into.
 */
import type { VaultDocumentFolderDto, VaultFileDialogArea } from '@/lib/api';
import { ARCHE_SAVES_ROOT, vaultAreaOfPath } from '@/lib/vault-areas';

export type FileDialogLocation =
  | { kind: 'folder'; area: VaultFileDialogArea; folderId: string | null }
  | { kind: 'recent' };

export const DOCUMENTS_ROOT: FileDialogLocation = {
  kind: 'folder',
  area: 'documents',
  folderId: null,
};
export const ARCHE_SAVES_HOME: FileDialogLocation = {
  kind: 'folder',
  area: 'arche-saves',
  folderId: null,
};
export const RECENT: FileDialogLocation = { kind: 'recent' };

export const AREA_LABELS: Record<VaultFileDialogArea, string> = {
  documents: 'Documents',
  'arche-saves': 'Arche Saves',
};

export function sameLocation(a: FileDialogLocation, b: FileDialogLocation): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'recent' || b.kind === 'recent') return true;
  return a.area === b.area && a.folderId === b.folderId;
}

/** The `/Arche Saves` folder itself, when the workspace has one. */
export function archeSavesRootFolder(
  folders: readonly VaultDocumentFolderDto[],
): VaultDocumentFolderDto | undefined {
  return folders.find((f) => f.path === ARCHE_SAVES_ROOT);
}

/**
 * The folder id to LIST for a location: `null` = the Holder root, a `vdf_*`
 * id = that folder, `undefined` = no single folder (Recent, or an Arche Saves
 * that has not been provisioned — nothing to list).
 */
export function listingFolderIdOf(
  location: FileDialogLocation,
  folders: readonly VaultDocumentFolderDto[],
): string | null | undefined {
  if (location.kind === 'recent') return undefined;
  if (location.folderId) return location.folderId;
  if (location.area === 'documents') return null;
  return archeSavesRootFolder(folders)?.folderId;
}

/** The sub-folders shown at a location, by name. Documents' root never shows
 *  the two system roots — Arche Saves has its own sidebar entry, and My-Fias
 *  is never shown. */
export function childFoldersAt(
  location: FileDialogLocation,
  folders: readonly VaultDocumentFolderDto[],
): VaultDocumentFolderDto[] {
  if (location.kind === 'recent') return [];
  const parentId = listingFolderIdOf(location, folders);
  if (parentId === undefined) return [];
  return folders
    .filter(
      (f) =>
        (f.parentFolderId ?? null) === parentId &&
        vaultAreaOfPath(f.path) === location.area &&
        f.path !== ARCHE_SAVES_ROOT,
    )
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

export interface Crumb {
  label: string;
  location: FileDialogLocation;
}

/** "Documents › Projects › 2026" — area root first, current folder last. */
export function crumbsFor(
  location: FileDialogLocation,
  folders: readonly VaultDocumentFolderDto[],
): Crumb[] {
  if (location.kind === 'recent') return [{ label: 'Recent', location }];
  const root: Crumb = {
    label: AREA_LABELS[location.area],
    location: { kind: 'folder', area: location.area, folderId: null },
  };
  if (!location.folderId) return [root];
  const byId = new Map(folders.map((f) => [f.folderId, f]));
  const chain: Crumb[] = [];
  const stop = location.area === 'arche-saves' ? archeSavesRootFolder(folders)?.folderId : null;
  let current = byId.get(location.folderId);
  // Bounded walk: a malformed parent loop must not hang the dialog.
  for (let guard = 0; current && current.folderId !== stop && guard < 64; guard += 1) {
    chain.unshift({
      label: current.name,
      location: { kind: 'folder', area: location.area, folderId: current.folderId },
    });
    current = current.parentFolderId ? byId.get(current.parentFolderId) : undefined;
  }
  return [root, ...chain];
}

/** One level up, or `null` at an area root / Recent. */
export function parentLocationOf(
  location: FileDialogLocation,
  folders: readonly VaultDocumentFolderDto[],
): FileDialogLocation | null {
  const crumbs = crumbsFor(location, folders);
  return crumbs.length > 1 ? crumbs[crumbs.length - 2].location : null;
}

/** Where a listed file sits, for search and Recent results ("Documents /
 *  Projects", "Arche Saves / AI Images"). */
export function describeFolderPath(folderPath: string | null): string {
  if (!folderPath) return 'Documents';
  if (vaultAreaOfPath(folderPath) === 'arche-saves') {
    return `Arche Saves${folderPath.slice(ARCHE_SAVES_ROOT.length).replace(/\//g, ' / ')}`;
  }
  return `Documents${folderPath.replace(/\//g, ' / ')}`;
}

/** Documents-area folders as an indented list, for the Save dialog's
 *  location menu at phone width. */
export function documentsFolderOutline(
  folders: readonly VaultDocumentFolderDto[],
): Array<{ folder: VaultDocumentFolderDto; depth: number }> {
  const inDocuments = folders.filter((f) => vaultAreaOfPath(f.path) === 'documents');
  const byParent = new Map<string | null, VaultDocumentFolderDto[]>();
  for (const folder of inDocuments) {
    const key = folder.parentFolderId ?? null;
    byParent.set(key, [...(byParent.get(key) ?? []), folder]);
  }
  const out: Array<{ folder: VaultDocumentFolderDto; depth: number }> = [];
  const walk = (parent: string | null, depth: number) => {
    const children = (byParent.get(parent) ?? []).sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    );
    for (const child of children) {
      out.push({ folder: child, depth });
      if (depth < 32) walk(child.folderId, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}
